// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useCallback, useMemo } from 'react';
import { useSendToTrader } from '@advisor-app/features/operations';
import { useAdvisorRole } from '@advisor-app/hooks/use-advisor-role';
import {
  type SourceAsset,
  type TransferOperationData,
  type BuyOperationData,
  type SellOperationData,
  type FundSearchResult,
} from '@shared/portfolio-operations';
import { useAggregationData } from '@shared/advisory/aggregation/providers';
import { isFundLikeHoldingAssetType } from '@shared/data-access/utils';
import { OperationType } from '@shared/data-access/interfaces/operation-type';
import { type AccountsHoldingsData } from '@shared/data-access/interfaces/api/brokerage/brokerage';

export const useAssetOperations = () => {
  const { accountsPositions } = useAggregationData();
  const { sendTradesTickers, sendTradeTicker } = useSendToTrader();
  const { canUseTrader } = useAdvisorRole();

  const portfolioFunds: FundSearchResult[] = useMemo(() => {
    if (accountsPositions.holdings.length === 0) {
      return [];
    }

    const filteredHoldings = accountsPositions.holdings.filter(
      holding =>
        (holding.ticker || holding.isin) &&
        isFundLikeHoldingAssetType(holding.asset_type),
    );

    const uniqueFundsMap = filteredHoldings.reduce((map, holding) => {
      const identifier = holding.ticker ?? holding.isin!;
      if (!map.has(identifier)) {
        return new Map(map).set(identifier, holding);
      }
      return map;
    }, new Map<string, AccountsHoldingsData>());

    return Array.from(uniqueFundsMap.values()).map(holding => ({
      ticker: holding.ticker ?? holding.isin!,
      name: holding.name,
      asset_type: holding.asset_type,
      url_logo: holding.details?.url_logo,
      url_morningstar: holding.details?.url_morningstar,
      isin: holding.isin!,
    }));
  }, [accountsPositions.holdings]);

  const onTransfer = useCallback(
    (data: TransferOperationData, sourceAsset: SourceAsset) => {
      if (!canUseTrader) return;

      // A full refund goes by units: the amount is their value rounded to cents,
      // and the simulation turns it back into more units than the position holds.
      const isFullRefund = data.type === 'full';
      const sourceShares = isFullRefund ? sourceAsset.quantity : data.shares;

      const trades = data.destinationFunds.map(fund => {
        const contracts_number = sourceShares * (fund.percentage / 100);

        return {
          operation: OperationType.TRANSFER as const,
          ticker: sourceAsset.ticker ?? sourceAsset.isin,
          name: sourceAsset.name,
          volume: fund.amount,
          contracts_number,
          asset_type: sourceAsset.assetType,
          institution_name: sourceAsset.institution.name,
          institution_id: sourceAsset.institution.id,
          account_id: sourceAsset.accountId,
          transfer_target: fund.ticker ?? fund.isin,
          inputType: isFullRefund ? ('shares' as const) : data.inputType,
        };
      });

      sendTradesTickers(...trades);
    },
    [canUseTrader, sendTradesTickers],
  );

  const onBuy = useCallback(
    (data: BuyOperationData, sourceAsset: SourceAsset) => {
      if (!canUseTrader) return;

      sendTradeTicker({
        operation: OperationType.BUY,
        ticker: sourceAsset.ticker ?? sourceAsset.isin,
        name: sourceAsset.name,
        volume: data.amount,
        contracts_number: data.shares,
        asset_type: sourceAsset.assetType,
        institution_name: sourceAsset.institution.name,
        institution_id: sourceAsset.institution.id,
        account_id: sourceAsset.accountId,
        inputType: data.inputType,
      });
    },
    [canUseTrader, sendTradeTicker],
  );

  const onSell = useCallback(
    (data: SellOperationData, sourceAsset: SourceAsset) => {
      if (!canUseTrader) return;

      sendTradeTicker({
        operation: OperationType.SELL,
        ticker: sourceAsset.ticker ?? sourceAsset.isin,
        name: sourceAsset.name,
        volume: data.amount,
        contracts_number: data.shares,
        asset_type: sourceAsset.assetType,
        institution_name: sourceAsset.institution.name,
        institution_id: sourceAsset.institution.id,
        account_id: sourceAsset.accountId,
        inputType: data.inputType,
      });
    },
    [canUseTrader, sendTradeTicker],
  );

  return {
    onTransfer,
    onBuy,
    onSell,
    portfolioFunds,
    canUseTrader,
  } as const;
};
